import { useNavigate } from 'react-router';
import { z } from 'zod';
import type { Alpaca } from '../models/Alpaca';
import type { Breed } from '../models/Breed';
import type { Color } from '../models/Color';
import type { Farm } from '../models/Farm';
import type { Gender } from '../models/Gender';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import RHFInput from './RHF/RHFInput';
import { RHFSelect } from './RHF/RHFSelect';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import { RHFTextArea } from './RHF/RHFTextArea';
import { format } from 'date-fns';
import AlpacaApi from '../apis/alpaca_api';
import { handleError } from '../apis';
import { toast } from 'react-toastify';
import { numberOrNull } from '../helpers';
import { useQueryClient } from '@tanstack/react-query';

const validator_alpaca = z.object({
  active: z.boolean(),
  alpacaId: z.number().optional(),
  applicationUserId: z.string().optional(),
  name: z.string('Required!').trim().min(1, 'Required'),
  dateOfBirth: z.string().optional().nullable(),
  colorId: z.number().nullable(),
  genderId: z.number().optional().nullable(),
  breedId: z.number().optional().nullable(),
  registryNumber: z.string().trim().optional().nullable(),
  farmId: z.number().optional().nullable(),
  microchipId: z.string().trim().nullable(),
  microchipLocation: z.string().trim().nullable(),
  note: z.string().trim().nullable(),
  tagNumber: z.string().trim().nullable(),
});

export default function AddUpdateAlpacaForm({
  farms,
  colors,
  breeds,
  genders,
  alpaca,
}: {
  farms: Farm[];
  colors: Color[];
  breeds: Breed[];
  genders: Gender[];
  alpaca: Alpaca;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(validator_alpaca),
    defaultValues: {
      ...alpaca,
      dateOfBirth: alpaca.dateOfBirth
        ? format(new Date(alpaca.dateOfBirth), 'yyyy-MM-dd')
        : null,
    },
  });

  const activeValue = useWatch({ control, name: 'active' });

  const onHandleSubmit = async (values: z.infer<typeof validator_alpaca>) => {
    const toUpdate: Alpaca = {
      active: values.active,
      alpacaId: values.alpacaId ? values.alpacaId : 0,
      applicationUserId: values.applicationUserId
        ? values.applicationUserId
        : undefined,
      name: values.name,
      dateOfBirth: values.dateOfBirth ? values.dateOfBirth : null,
      colorId: values.colorId ? values.colorId : null,
      genderId: values.genderId ? values.genderId : null,
      breedId: values.breedId ? values.breedId : null,
      registryNumber: values.registryNumber ? values.registryNumber : null,
      farmId: values.farmId ? values.farmId : null,
      microchipId: values.microchipId ? values.microchipId : null,
      microchipLocation: values.microchipLocation
        ? values.microchipLocation
        : null,
      note: values.note ? values.note : null,
      tagNumber: values.tagNumber ? values.tagNumber : null,
    };

    let res: Alpaca;

    try {
      if (toUpdate.alpacaId === 0) {
        res = await AlpacaApi.createAlpaca(toUpdate);
      } else {
        res = await AlpacaApi.updateAlpaca(toUpdate);
      }
      queryClient.invalidateQueries();
      navigate(`/alpacas/${res.alpacaId}`);
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onHandleSubmit)}
      className='flex flex-col items-center justify-center'
    >
      <div className={`mb-2 flex py-2`}>
        <div
          className={`min-w-[275px] flex justify-center  rounded-lg bg-gray-100 px-4 py-3 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-300`}
        >
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={activeValue}
              {...register('active')}
              id='active'
              className={`rounded border-gray-300 bg-gray-100 text-teal-600 focus:ring-2 focus:ring-teal-500 dark:border-gray-600 ${
                activeValue ? 'bg-teal-600 dark:bg-teal-600' : ''
              } dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-teal-600`}
            />
            Active
          </label>
        </div>
      </div>
      <div className='alpaca-form-grid'>
        <div className='sm:col-span-2'>
          <RHFInput
            name='name'
            label='Name'
            type='text'
            register={register('name')}
            errors={errors}
          />
        </div>

        <RHFInput
          name='dateOfBirth'
          label='Date Of Birth'
          type='date'
          className='min-w-[275px]'
          register={register('dateOfBirth')}
          errors={errors}
        />
        <RHFSelect
          name='genderId'
          label='Gender'
          options={genders}
          optionMethod={(genders: Gender[]) =>
            genders.map((g) => (
              <option value={g.genderId} key={g.genderId}>
                {g.genderName}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('genderId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
        <RHFSelect
          name='colorId'
          label='Color'
          options={colors}
          optionMethod={(colors: Color[]) =>
            colors.map((g) => (
              <option value={g.colorId} key={g.colorId}>
                {g.colorName}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('colorId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
        <RHFSelect
          name='breedId'
          label='Breed'
          options={breeds}
          optionMethod={(breeds: Breed[]) =>
            breeds.map((g) => (
              <option value={g.breedId} key={g.breedId}>
                {g.breedName}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('breedId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
        <RHFInput
          name='registryNumber'
          label='Registry Number'
          type='text'
          className='min-w-[275px]'
          register={register('registryNumber')}
          errors={errors}
        />
        <RHFSelect
          name='farmId'
          label='Farms'
          options={farms}
          optionMethod={(farms: Farm[]) =>
            farms.map((g) => (
              <option value={g.farmId} key={g.farmId}>
                {g.name}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('farmId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
        <RHFInput
          name='tagNumber'
          label='Tag Number'
          type='text'
          className='min-w-[275px]'
          register={register('tagNumber')}
          errors={errors}
        />
        <RHFInput
          name='microchipId'
          label='Microchip Id'
          type='text'
          className='min-w-[275px]'
          register={register('microchipId')}
          errors={errors}
        />
        <RHFInput
          name='microchipLocation'
          label='Microchip Location'
          type='text'
          className='min-w-[275px]'
          register={register('microchipLocation')}
          errors={errors}
        />
        <div />
        <div className='sm:col-span-2'>
          <RHFTextArea
            name='note'
            label='Note'
            register={register('note')}
            errors={errors}
            rows={3}
          />
        </div>
        <button
          type='button'
          onClick={() => {
            reset();
            navigate(-1);
          }}
          className='my-2 max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
        >
          Cancel
        </button>
        <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
      </div>
    </form>
  );
}
